import { type ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthBootstrap } from '@foundation-bb/foundation-auth';
import { rysApi, useGetAuthConfigQuery } from '@/api/rysApi';
import { refreshToken, setAuthLoading, setMe, selectAccessToken } from '@/store/authSlice';
import { store } from '@/store/store';
import type { AppDispatch } from '@/store/store';

/**
 * Cold-load auth bootstrap + proactive pre-expiry refresh, on Foundation's hook.
 *
 * H3 payoff: the non-HttpOnly session-hint cookie name is NOT hardcoded here — it
 * comes from `/api/config/auth` (`sessionHintCookie`). The backend owns the name;
 * the frontend reads it so the two can never drift. While the config is still
 * loading we render nothing of our own (the Foundation auth slice starts in
 * `isLoading: true`, so route guards wait); once it arrives we mount the inner
 * component that actually runs the bootstrap with the resolved cookie name.
 */
function Bootstrap({ hintCookieName, children }: { hintCookieName: string; children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();

  useAuthBootstrap({
    hintCookieName,
    getAccessToken: () => selectAccessToken(store.getState()),
    refresh: async () => {
      const res = await dispatch(refreshToken());
      // After a successful refresh, load the rich profile (display name lives in
      // `/api/me`, not the JWT) so `selectIsAuthenticated` can flip true.
      if (refreshToken.fulfilled.match(res) && res.payload) {
        try {
          const me = await dispatch(
            rysApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }),
          ).unwrap();
          dispatch(setMe(me));
        } catch {
          // getMe failed despite a fresh token — leave me unset; the guard keeps
          // the user out and the next interaction surfaces a real diagnosis.
        }
      }
    },
    setLoadingDone: () => dispatch(setAuthLoading(false)),
  });

  return <>{children}</>;
}

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const { data: authConfig, isError } = useGetAuthConfigQuery();

  // Escape hatch: if /api/config/auth cannot be fetched (endpoint down / never
  // resolves), the auth slice would otherwise stay `isLoading: true` forever and
  // hang every protected route on the spinner. On a hard config error, release
  // the loading state — the user is treated as anonymous (no hint cookie to gate
  // a cold-load refresh on anyway) instead of hanging.
  useEffect(() => {
    if (isError) dispatch(setAuthLoading(false));
  }, [isError, dispatch]);

  // Hold rendering of the bootstrap until we know the hint-cookie name from the
  // backend. The auth slice is `isLoading: true` meanwhile, so guards wait.
  if (!authConfig?.sessionHintCookie) return <>{children}</>;

  return (
    <Bootstrap hintCookieName={authConfig.sessionHintCookie} key={authConfig.sessionHintCookie}>
      {children}
    </Bootstrap>
  );
}
